import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";

export const PERMS = [
  { k: "add", l: "Add entries" },
  { k: "edit", l: "Edit entries" },
  { k: "delete", l: "Delete entries" },
  { k: "sett", l: "NEW SETT sheet" },
  { k: "export", l: "Export, print & share" },
  { k: "books", l: "Create, rename & delete books" },
  { k: "import", l: "Import files" },
];

const empty = { name: "", email: "", password: "", active: true, permissions: { add: true, edit: true }, book_ids: [] };

const Text = ({ label, testId, ...p }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    <input data-testid={testId} {...p} className="mt-1.5 w-full h-11 px-3 border border-slate-300 outline-none focus:border-slate-900" />
  </label>
);

export const StaffDialog = ({ open, onOpenChange, staff, books, onSaved }) => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(staff ? { ...staff, password: "" } : empty);
  }, [open, staff]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const togglePerm = (k, v) => setF((p) => ({ ...p, permissions: { ...p.permissions, [k]: v } }));
  const toggleBook = (id, v) => setF((p) => ({ ...p, book_ids: v ? [...p.book_ids, id] : p.book_ids.filter((x) => x !== id) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { name: f.name, email: f.email, active: f.active, permissions: f.permissions, book_ids: f.book_ids,
      password: f.password || null };
    try {
      staff ? await api.put(`/staff/${staff.id}`, body) : await api.post("/staff", body);
      toast.success(staff ? "Staff updated" : "Staff added");
      onSaved();
      onOpenChange(false);
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg max-h-[92vh] overflow-y-auto" data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight">{staff ? "Edit staff" : "Add staff"}</DialogTitle>
          <DialogDescription>They sign in with this email and password and only see the books you tick.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Text label="Name" testId="staff-name-input" required maxLength={60} value={f.name} onChange={(e) => set("name", e.target.value)} />
            <Text label="Email" testId="staff-email-input" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <Text label={staff ? "New password (leave blank to keep)" : "Password (min 8)"} testId="staff-password-input" type="password"
            required={!staff} minLength={8} autoComplete="new-password" value={f.password} onChange={(e) => set("password", e.target.value)} />
          <label className="flex items-center justify-between h-11 px-3 border border-slate-300 bg-white">
            <span className="text-sm font-semibold">Account active</span>
            <Switch data-testid="staff-active-switch" checked={f.active} onCheckedChange={(v) => set("active", v)} />
          </label>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Permissions</p>
            <div className="border border-slate-300 divide-y divide-slate-200">
              {PERMS.map((p) => (
                <label key={p.k} className="flex items-center justify-between px-3 h-11 bg-white cursor-pointer">
                  <span className="text-sm">{p.l}</span>
                  <Switch data-testid={`staff-perm-${p.k}`} checked={Boolean(f.permissions[p.k])} onCheckedChange={(v) => togglePerm(p.k, v)} />
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">Viewing the assigned books' daily sheet and monthly summary is always allowed.</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">Books they can open</p>
            {books.length === 0 ? <p className="text-sm text-slate-500">No books yet.</p> : (
              <div className="border border-slate-300 divide-y divide-slate-200 max-h-48 overflow-y-auto">
                {books.map((b) => (
                  <label key={b.id} className="flex items-center gap-3 px-3 h-11 bg-white cursor-pointer">
                    <Checkbox data-testid={`staff-book-${b.id}`} checked={f.book_ids.includes(b.id)} onCheckedChange={(v) => toggleBook(b.id, Boolean(v))} />
                    <span className="text-sm font-semibold uppercase">{b.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button data-testid="staff-save-btn" disabled={busy}
            className="w-full h-12 bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60">
            {busy ? "Saving…" : staff ? "Save changes" : "Add staff"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
